/**
 * WarRoomView (P157) -- the resilience war-room.
 *
 * Pick a severe-but-plausible scenario; we run it through the live
 * topology and judge each impacted IBS against its impact tolerance
 * (tightest supporting-service RTO). Board-ready "are we within
 * tolerance?" verdict + the weakest link per breach.
 */
import { canSignAs } from "shared/api/authority";
import { toleranceSentence } from "shared/tolerance/toleranceSentence";
import {
  durationProvenanceSentence,
  isBankFigure,
  sourceFigurePhrase,
} from "twin/scenarios/durationProvenance";
import { useState } from "react";
import { Bdi } from "shared/components/Bdi";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { seatHolder, useSeatHolders } from "shared/api/useSeatHolders";

import { EmptyState } from "shared/components/EmptyState";

import { ToleranceBadge } from "shared/components/ToleranceBadge";
import type { ToleranceStatus } from "shared/tolerance/useToleranceState";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatSAR, MONEY_NOT_MEASURED } from "shared/money/money";
import type {
  CheapestFixReport,
  FixCandidate,
  ToleranceReport,
  ToleranceRow,
} from "shared/api/types";
import {
  useScenarioCheapestFix,
  useScenarios,
  useScenarioTolerance,
} from "twin/api/scenario_hooks";
import { hasPosition, useMe } from "shared/api/useMe";
import { useDownloadWarRoomPack } from "twin/warroom/useDownloadWarRoomPack";
import { useToast } from "shared/toast/ToastProvider";
import { useLiveTolerance } from "shared/tolerance/useLiveTolerance";
import { formatDateTime } from "shared/time/formatDate";

type CostBasis = "hours" | "sar";

/** Format a Decimal-as-string SAR figure, "--" when uncosted/null. */
function sarOrDash(raw?: string | null): string {
  const out = formatSAR(raw ?? null);
  return out === MONEY_NOT_MEASURED ? "--" : out;
}

const VERDICT_PILL: Record<string, string> = {
  breached: "bg-rag-redBg text-rag-red",
  at_risk: "bg-rag-amberBg text-rag-amber",
  within: "bg-rag-greenBg text-rag-green",
  unknown: "bg-slate-100 text-slate-600",
};

function VerdictPill({ verdict }: { verdict: string }) {
  const { t } = useTranslation();
  return (
    <span
      data-testid="war-room-verdict"
      data-verdict={verdict}
      className={`rounded px-2 py-0.5 text-xs font-medium ${VERDICT_PILL[verdict] ?? ""}`}
    >
      {t(`modules.twin.warRoom.verdict.${verdict}`)}
    </span>
  );
}

const LIVE_ORDER: Record<string, number> = { breached: 0, at_risk: 1 };

/** Round 20 U6 -- a duty manager arriving at the war room was taught
 *  what-if before now. What is live leads: each service breaching or at
 *  risk under current conditions, from which live condition, and the
 *  breach finding the monitor raised. With nothing live this renders
 *  nothing and the scenario picker reads as it always has. */
function LiveNowPanel() {
  const { t, i18n } = useTranslation();
  const live = useLiveTolerance();
  const rows = (live.data?.rows ?? [])
    .filter((r) => r.verdict in LIVE_ORDER)
    .sort(
      (a, b) =>
        (LIVE_ORDER[a.verdict] ?? 9) - (LIVE_ORDER[b.verdict] ?? 9) ||
        a.ibs_name.localeCompare(b.ibs_name),
    );
  if (rows.length === 0) return null;
  return (
    <article
      data-testid="war-room-live-now"
      className="rounded-lg border border-rag-red/30 bg-white p-4"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-rag-red">
        {t("modules.twin.warRoom.liveNow.heading")}
      </p>
      <p className="mt-1 text-sm text-slate-700">
        {t("modules.twin.warRoom.liveNow.summary", {
          breached: live.data?.breached ?? 0,
          atRisk: rows.length - (live.data?.breached ?? 0),
        })}
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {rows.map((r) => (
          <li
            key={r.ibs_service_id}
            data-testid={`war-room-live-row-${r.ibs_code}`}
            className="flex flex-wrap items-baseline gap-x-2 text-sm"
          >
            <VerdictPill verdict={r.verdict} />
            <Link
              to={`/twin/ibs/${r.ibs_service_id}`}
              className="font-medium text-slate-800 hover:underline"
            >
              {r.ibs_name}
            </Link>
            {r.via && (
              <span className="text-slate-600">
                {t("modules.twin.warRoom.liveNow.via", { via: r.via })}
              </span>
            )}
            {r.projected_recovery_hours != null && (
              <span className="tabular-nums text-slate-500">
                {t("modules.twin.warRoom.liveNow.projected", {
                  projected: r.projected_recovery_hours,
                  tolerance: r.impact_tolerance_hours,
                })}
              </span>
            )}
            {r.verdict === "breached" && r.breach_since && (
              <span className="text-slate-500">
                {t("modules.twin.warRoom.liveNow.since", {
                  since: formatDateTime(r.breach_since, i18n.language),
                })}
              </span>
            )}
            {r.verdict === "breached" && r.breach_finding_id && (
              <Link
                data-testid={`war-room-live-finding-${r.ibs_code}`}
                to={`/twin/findings/${r.breach_finding_id}`}
                className="text-brand-brassDark hover:underline"
              >
                {t("modules.twin.warRoom.liveNow.finding")}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </article>
  );
}

/** P180: effective impact tolerance + whether it's declared or a modelled
 * (RTO-proxy) fallback, so the operator knows the fidelity. */
function ToleranceCell({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  const tol = row.impact_tolerance_hours ?? row.tolerance_rto_hours;
  if (tol == null) {
    return (
      <span className="text-slate-400">{t("modules.twin.warRoom.noTolerance")}</span>
    );
  }
  const declared = row.tolerance_source === "declared";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-slate-700">
        {t("modules.twin.warRoom.hours", { n: tol })}
      </span>
      {/* B1 -- the state, in the words every other surface uses.
          This badge used to read "DECLARED" whenever the number came
          from the column rather than an RTO proxy, which says nothing
          about whether anyone signed for it -- so a row read "2h
          DECLARED" beneath a headline saying "0 of 2 governed". Both
          were true; one word was doing two jobs. `tolerance_source`
          still earns its place, but only for the fidelity question
          below: is this a declared figure at all, or a model's
          stand-in? */}
      {declared ? (
        <ToleranceBadge
          state={{
            ibs_service_id: row.ibs_service_id,
            ibs_code: "",
            ibs_name: row.ibs_name,
            mvb: row.mvb_flag,
            hours: row.impact_tolerance_hours ?? null,
            status: (row.tolerance_status ?? "set_unsigned") as ToleranceStatus,
            signed_by: row.tolerance_provenance ?? null,
            proposed_hours: row.tolerance_proposed_hours ?? null,
            awaiting_role: row.tolerance_awaiting_role ?? null,
            awaiting_since: row.tolerance_awaiting_since ?? null,
            owner_recorded: row.tolerance_owner_recorded ?? true,
          }}
        />
      ) : (
        <span
          data-testid="war-room-tolerance-source"
          data-source={row.tolerance_source ?? "none"}
          className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500"
        >
          {t("modules.twin.warRoom.toleranceModelled")}
        </span>
      )}
      <WindowAndLimitNote row={row} />
    </span>
  );
}

/** P417 -- which declared window is tightening this tolerance right
 *  now, and the line the bank drew inside it.
 *
 *  Both render nothing at all when neither is declared, which is every
 *  IBS before P417. The window note shows the BASE beside the tightened
 *  figure, because a reader needs to see what the window did rather
 *  than only its result -- a row that silently reads 2h where it read
 *  8h yesterday is a number nobody can account for. */
function WindowAndLimitNote({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  const hasWindow = row.active_window != null && row.base_tolerance_hours != null;
  if (!hasWindow && row.limit_hours == null) return null;
  return (
    <>
      {hasWindow && (
        <span
          data-testid="war-room-active-window"
          data-window={row.active_window}
          title={t("modules.twin.warRoom.activeWindowTip")}
          className="rounded bg-rag-amber/10 px-1 py-0.5 text-[9px] font-semibold text-rag-amber"
        >
          {t("modules.twin.warRoom.activeWindow", {
            label: row.active_window,
            base: row.base_tolerance_hours,
          })}
        </span>
      )}
      {row.limit_hours != null && (
        <span
          data-testid="war-room-limit"
          data-limit={row.limit_hours}
          title={t("modules.twin.warRoom.limitTip")}
          className="text-[10px] italic text-slate-500"
        >
          {t("modules.twin.warRoom.limitLine", { n: row.limit_hours })}
        </span>
      )}
    </>
  );
}

/** P180: the timed clock -- "breaches in T+Xh, recovers +Yh" when it
 * breaches, else the headroom. */
function ClockCell({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  const breachesAt = row.breaches_at_hours;
  const recoversAt = row.recovers_at_hours;
  if (breachesAt != null) {
    return (
      <span
        data-testid="war-room-clock"
        data-breaches-at={breachesAt}
        className="text-rag-red"
      >
        {t("modules.twin.warRoom.breachesIn", { n: breachesAt })}
        {recoversAt != null && (
          <span className="ms-1 text-slate-400">
            {t("modules.twin.warRoom.recoversAt", { n: recoversAt })}
          </span>
        )}
      </span>
    );
  }
  if (row.headroom_hours != null) {
    return (
      <span
        data-testid="war-room-clock"
        data-breaches-at="none"
        className="text-rag-green"
      >
        {t("modules.twin.warRoom.headroom", { n: row.headroom_hours })}
      </span>
    );
  }
  return <span className="text-slate-400">--</span>;
}

/** P183: a tiny inline sparkline of the recovery curve -- a step cliff
 * (single dependency) vs a gradual ramp (redundant). */
function RecoverySparkline({ points }: { points: [number, number][] }) {
  const W = 56;
  const H = 16;
  if (points.length < 2) {
    return (
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <line x1="0" y1="2" x2={W} y2="2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  const maxH = points[points.length - 1]?.[0] || 1;
  const path = points
    .map(([h, pct]) => {
      const x = (h / maxH) * W;
      const y = H - (pct / 100) * (H - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline
        points={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** P418 -- what the SCENARIO stated about this disruption, beside the
 *  number the recovery model derived.
 *
 *  The two are different claims and the row used to carry only the
 *  second. Three of SAMA's mandated nine state no duration at all, a
 *  fourth says the outage lasts until the failed update is fixed, and a
 *  fifth gives a RANGE -- so "no figure" needed a reason attached to it
 *  rather than a blank, and a range needed both of its ends.
 *
 *  Renders nothing at all when the scenario stated nothing, which is
 *  every scenario that existed before P418. */
function StatedDurationNote({ row }: { row: ToleranceRow }) {
  const { t, i18n } = useTranslation();
  /* Round 16 D18 -- whose the figure is: "scenario states 24h" read the
     same whether SAMA wrote it or the bank did. */
  const prov = {
    set_by: row.stated_set_by,
    set_at: row.stated_set_at,
    source_label: row.stated_source_label,
    source_basis: row.stated_source_basis,
    source_hours: row.stated_source_hours,
    source_hours_max: row.stated_source_hours_max,
    basis: row.stated_duration_basis,
    hours: row.stated_duration_hours,
    hours_max: row.stated_duration_hours_max,
  };
  const provenance = durationProvenanceSentence(t, i18n.language, prov);
  const bankFigure = isBankFigure(prov);
  const basis = row.stated_duration_basis;
  if (basis == null || basis === "unstated") return null;
  if (basis === "until_recovered") {
    return (
      <span
        data-testid="war-room-stated-duration"
        data-basis="until_recovered"
        className="ms-1 text-[10px] italic text-slate-400"
      >
        {t("modules.twin.warRoom.statedUntilRecovered")}
      </span>
    );
  }
  const lower = row.stated_duration_hours;
  if (lower == null) return null;
  const upper = row.stated_duration_hours_max;
  const stated = upper != null && upper > lower ? upper : lower;
  // Round 14 D9 -- why, in words. The only explanation was an amber
  // "scenario states 72h" and a tooltip, and nothing at all when
  // recovery outlasted the stated duration.
  /* Round 16 D20 -- a composite member at +2h stating 4h raises the
     outage to 6h, and this read "raised to 4h", naming neither the
     member's start nor the figure the floor actually is. And a range
     read "The scenario states 12h" for SAMA's 5 to 12 hours. */
  const onset = row.stated_onset_hours ?? 0;
  const raised = row.stated_envelope_hours ?? stated;
  const source = row.stated_source_label ?? t("modules.twin.warRoom.theScenario");
  const why = row.outage_floored_by_statement
    ? onset > 0
      ? t("modules.twin.warRoom.statedWhyFlooredOffset", {
          raised,
          onset,
          source,
          n: stated,
        })
      : bankFigure
        ? t("modules.twin.warRoom.statedWhyFlooredBank", {
            n: stated,
            who: row.stated_set_by ?? "",
            source: row.stated_source_label ?? t("modules.twin.warRoom.theScenario"),
            figure: sourceFigurePhrase(t, prov),
          })
        : upper != null && upper > lower
          ? t("modules.twin.warRoom.statedWhyFlooredRange", { from: lower, to: upper })
          : t("modules.twin.warRoom.statedWhyFloored", { n: stated })
    : row.projected_outage_hours > stated
      ? onset > 0
        ? t("modules.twin.warRoom.statedWhyOutlastsOffset", {
            n: stated,
            onset,
            source,
          })
        : t("modules.twin.warRoom.statedWhyOutlasts", { n: stated })
      : null;
  return (
    <>
      <span
        data-testid="war-room-stated-duration"
        data-basis="stated"
        data-floored={row.outage_floored_by_statement === true ? "true" : "false"}
        title={t("modules.twin.warRoom.statedDurationTip")}
        className={`ms-1 text-[10px] italic ${
          row.outage_floored_by_statement ? "text-rag-amber" : "text-slate-400"
        }`}
      >
        {provenance && !bankFigure
          ? provenance
          : provenance
            ? `${upper != null && upper > lower ? t("scenarioProvenance.range", { from: lower, to: upper }) : t("scenarioProvenance.hours", { n: lower })} \u2014 ${provenance}`
            : upper != null && upper > lower
              ? t("modules.twin.warRoom.statedRange", { from: lower, to: upper })
              : t("modules.twin.warRoom.statedDuration", { n: lower })}
      </span>
      {why && (
        <span
          data-testid="war-room-stated-why"
          className="block text-[10px] text-slate-500"
        >
          {why}
        </span>
      )}
    </>
  );
}

/** P418 -- how much of the target the source says is lost.
 *
 *  Separate from severity and never derived from it: a bank may
 *  legitimately call a 60 per cent loss `lost` because the remaining 40
 *  cannot clear settlement. An absent figure renders NOTHING rather
 *  than a zero, because the two are not the same claim. */
function CapacityLossNote({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  if (row.capacity_loss_pct == null) return null;
  return (
    <span
      data-testid="war-room-capacity-loss"
      className="ms-1 text-[10px] text-slate-500"
    >
      {t("modules.twin.warRoom.capacityLoss", { pct: row.capacity_loss_pct })}
    </span>
  );
}

/** Round 14 D8 -- every figure the scenario stated, under the target it
 *  was stated for.
 *
 *  SAMA's 100 per cent of the mobile and web channels read as 100 per
 *  cent of a business service whose branches the source says kept
 *  working; one settlement service's 40 per cent landed on ten IBSes;
 *  and the ransomware's 72h was paired with the DDoS member's 55 per
 *  cent while the DDoS's own 9h was never shown. Each is a separate
 *  statement about a separate target, and is printed as one. */
function StatedFiguresNote({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  const figures = Array.isArray(row.stated_figures) ? row.stated_figures : [];
  const shown = figures.filter(
    (f) =>
      f.capacity_loss_pct != null ||
      f.duration_hours != null ||
      f.duration_basis === "until_recovered",
  );
  if (shown.length === 0) return null;
  return (
    <ul data-testid="war-room-stated-figures" className="mt-0.5 space-y-0">
      {shown.map((f) => {
        const duration =
          f.duration_basis === "until_recovered"
            ? t("modules.twin.warRoom.figureUntilRecovered")
            : f.duration_hours != null
              ? f.duration_hours_max != null && f.duration_hours_max > f.duration_hours
                ? t(
                    f.set_by_bank
                      ? "modules.twin.warRoom.figureRangeSet"
                      : "modules.twin.warRoom.figureRange",
                    { from: f.duration_hours, to: f.duration_hours_max },
                  )
                : t(
                    f.set_by_bank
                      ? "modules.twin.warRoom.figureHoursSet"
                      : "modules.twin.warRoom.figureHours",
                    { n: f.duration_hours },
                  )
              : t("modules.twin.warRoom.figureNoDuration");
        return (
          <li
            key={`${f.target_kind}:${f.target_name}`}
            data-testid="war-room-stated-figure"
            data-target={f.target_name}
            className="text-[10px] text-slate-500"
          >
            {f.capacity_loss_pct != null
              ? t(
                  /* Round 16 D22 -- a facility's loss is of its staff:
                     SAMA says "100% of staff", not that the head
                     office is 100% lost. */
                  f.target_kind === "facility"
                    ? "modules.twin.warRoom.figureWithStaffLoss"
                    : "modules.twin.warRoom.figureWithCapacity",
                  {
                    target: f.target_name,
                    duration,
                    pct: f.capacity_loss_pct,
                  },
                )
              : t("modules.twin.warRoom.figure", {
                  target: f.target_name,
                  duration,
                })}
          </li>
        );
      })}
    </ul>
  );
}

/** Round 16 H17 -- the headline's chips sat apart on screen and ran
 *  together in the text ("tolerance6 MVB2 of 6 governedfirst breach"),
 *  which is what a screen reader and a copy read. A separator that only
 *  the text carries; the margins still do the work on screen. */
function HeadlineSep() {
  const { t } = useTranslation();
  return <span className="sr-only">{t("modules.twin.warRoom.chipSeparator")}</span>;
}

/** P183: recovery shape -- profile chip + sparkline + cumulative
 * service-hours lost (a ramp loses half a step's). */
function RecoveryCell({ row }: { row: ToleranceRow }) {
  const { t } = useTranslation();
  const profile = row.recovery_profile ?? "step";
  const lost = row.service_hours_lost ?? 0;
  return (
    <span
      data-testid="war-room-recovery"
      data-profile={profile}
      className="inline-flex items-center gap-2"
    >
      <span className={profile === "linear" ? "text-rag-green" : "text-rag-amber"}>
        <RecoverySparkline points={row.recovery_curve ?? []} />
      </span>
      <span className="inline-flex flex-col leading-tight">
        <span
          data-testid="war-room-recovery-profile"
          title={t(`modules.twin.warRoom.recovery.${profile}Tip`)}
          className="text-[9px] font-semibold uppercase tracking-wide text-slate-500"
        >
          {t(`modules.twin.warRoom.recovery.${profile}`)}
          <span className="sr-only">
            {" "}
            {t(`modules.twin.warRoom.recovery.${profile}Tip`)}
          </span>
        </span>
        <span className="text-[10px] tabular-nums text-slate-600">
          {t("modules.twin.warRoom.recovery.lost", { n: lost })}
        </span>
      </span>
    </span>
  );
}

function CheapestFixPanel({
  fix,
  budget,
  onBudget,
  basis,
  onBasis,
}: {
  fix: CheapestFixReport;
  budget: number | null;
  onBudget: (b: number | null) => void;
  basis: CostBasis;
  onBasis: (b: CostBasis) => void;
}) {
  const { t } = useTranslation();
  const top: FixCandidate | undefined = fix.candidates[0];
  const planCostSar = sarOrDash(fix.plan.total_cost_sar);
  return (
    <article
      data-testid="war-room-fix"
      className="rounded-lg border border-brand-brass/40 bg-brand-brass/5 p-4"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-brassDark">
        {t("modules.twin.warRoom.cheapestFix.heading")}
      </p>
      {top ? (
        <>
          <p
            data-testid="war-room-fix-recommend"
            className="mt-1 text-sm text-slate-800"
          >
            {t("modules.twin.warRoom.cheapestFix.recommend", {
              service: top.service_name,
              current: top.current_rto_hours,
              target: top.target_rto_hours,
              cleared: top.ibs_cleared,
            })}
            {top.mvb_cleared > 0 && (
              <span className="ms-1 font-semibold text-brand-brassDark">
                {t("modules.twin.warRoom.cheapestFix.recommendMvb", {
                  count: top.mvb_cleared,
                })}
              </span>
            )}
          </p>
          <table className="mt-3 w-full text-start text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-1">
                  {t("modules.twin.warRoom.cheapestFix.colService")}
                </th>
                <th className="py-1">
                  {t("modules.twin.warRoom.cheapestFix.colChange")}
                </th>
                <th className="py-1">
                  {t("modules.twin.warRoom.cheapestFix.colCleared")}
                </th>
                <th className="py-1">{t("modules.twin.warRoom.cheapestFix.colMvb")}</th>
                <th className="py-1 text-end">
                  {t("modules.twin.warRoom.cheapestFix.colCost")}
                </th>
              </tr>
            </thead>
            <tbody>
              {fix.candidates.map((c) => (
                <tr
                  key={c.service_id}
                  data-testid={`war-room-fix-row-${c.service_id}`}
                  className="border-t border-brand-brass/20"
                >
                  <td className="py-1.5 text-slate-800">{c.service_name}</td>
                  <td className="py-1.5 tabular-nums text-slate-600">
                    {t("modules.twin.warRoom.cheapestFix.rtoChange", {
                      current: c.current_rto_hours,
                      target: c.target_rto_hours,
                    })}
                  </td>
                  <td className="py-1.5 tabular-nums text-slate-700">
                    {c.ibs_cleared}
                  </td>
                  <td className="py-1.5 tabular-nums text-brand-brassDark">
                    {c.mvb_cleared}
                  </td>
                  <td
                    data-testid={`war-room-fix-cost-${c.service_id}`}
                    className="py-1.5 text-end tabular-nums text-slate-700"
                  >
                    {sarOrDash(c.hardening_cost_sar)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {fix.plan.steps.length > 0 && (
            <div
              data-testid="war-room-plan"
              className="mt-4 border-t border-brand-brass/20 pt-3"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-brand-brassDark">
                {t("modules.twin.warRoom.fixPlan.heading")}
              </p>
              <p
                data-testid="war-room-plan-headline"
                className="mt-1 text-sm text-slate-800"
              >
                {t("modules.twin.warRoom.fixPlan.headline", {
                  services: fix.plan.steps.length,
                  cleared: fix.plan.total_ibs_cleared,
                  ...breachCut(fix, t),
                  mvbCut:
                    (fix.plan.total_mvb_functions_cleared ?? 0) > 0
                      ? t("modules.twin.warRoom.fixPlan.mvbCutFunctions", {
                          services:
                            fix.plan.total_mvb_cleared -
                            (fix.plan.total_mvb_functions_cleared ?? 0),
                          count: fix.plan.total_mvb_functions_cleared ?? 0,
                        })
                      : t("modules.twin.warRoom.fixPlan.mvbCut", {
                          mvb: fix.plan.total_mvb_cleared,
                        }),
                  hours: fix.plan.total_rto_delta_hours,
                })}
              </p>
              {fix.plan.total_cost_sar != null && (
                <p
                  data-testid="war-room-plan-cost"
                  className="mt-0.5 text-sm font-semibold text-brand-brassDark"
                >
                  {t("modules.twin.warRoom.fixPlan.totalCost", {
                    cost: planCostSar,
                  })}
                </p>
              )}
              <ol className="mt-2 flex flex-col gap-1">
                {fix.plan.steps.map((s, i) => (
                  <li
                    key={s.service_id}
                    data-testid={`war-room-plan-step-${s.service_id}`}
                    className="flex flex-wrap items-baseline gap-x-2 text-sm"
                  >
                    <span className="text-slate-400 tabular-nums">{i + 1}.</span>
                    <span className="font-medium text-slate-800">{s.service_name}</span>
                    <span className="tabular-nums text-slate-500">
                      {t("modules.twin.warRoom.cheapestFix.rtoChange", {
                        current: s.current_rto_hours,
                        target: s.target_rto_hours,
                      })}
                    </span>
                    <span className="text-brand-brassDark">
                      {t("modules.twin.warRoom.fixPlan.cumulative", {
                        count: s.cumulative_ibs_cleared,
                      })}
                    </span>
                    {s.hardening_cost_sar != null && (
                      <span
                        data-testid={`war-room-plan-step-cost-${s.service_id}`}
                        className="tabular-nums text-slate-500"
                      >
                        {sarOrDash(s.hardening_cost_sar)}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* P176/P178: budget-constrained "what can I afford?" by hours or SAR. */}
          <div className="mt-4 border-t border-brand-brass/20 pt-3">
            <div
              data-testid="war-room-basis-toggle"
              className="mb-2 inline-flex overflow-hidden rounded border border-slate-300 text-xs"
            >
              {(["hours", "sar"] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`war-room-basis-${b}`}
                  aria-pressed={basis === b}
                  onClick={() => onBasis(b)}
                  className={`px-2.5 py-1 ${
                    basis === b
                      ? "bg-brand-brass/15 font-semibold text-brand-brassDark"
                      : "bg-white text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t(`modules.twin.warRoom.budget.basis.${b}`)}
                </button>
              ))}
            </div>
            <label className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
              <span>
                {basis === "sar"
                  ? t("modules.twin.warRoom.budget.labelSar")
                  : t("modules.twin.warRoom.budget.label")}
              </span>
              <input
                data-testid="war-room-budget-input"
                type="number"
                min={0}
                step={basis === "sar" ? 100000 : 1}
                value={budget ?? ""}
                onChange={(e) =>
                  onBudget(e.target.value === "" ? null : Number(e.target.value))
                }
                className={`rounded border border-slate-300 px-2 py-1 text-sm ${
                  basis === "sar" ? "w-36" : "w-24"
                }`}
                placeholder={basis === "sar" ? "SAR" : "h"}
              />
            </label>
            {budget != null && budget > 0 && fix.budgeted && (
              <p
                data-testid="war-room-budget-result"
                className="mt-2 text-sm text-slate-800"
              >
                {fix.budgeted.cost_basis === "sar"
                  ? t("modules.twin.warRoom.budget.resultSar", {
                      cleared: fix.budgeted.ibs_cleared,
                      mvb: fix.budgeted.mvb_cleared,
                      services: fix.budgeted.chosen_service_names.length,
                      spend: sarOrDash(fix.budgeted.total_spend_sar),
                    })
                  : t("modules.twin.warRoom.budget.result", {
                      cleared: fix.budgeted.ibs_cleared,
                      mvb: fix.budgeted.mvb_cleared,
                      services: fix.budgeted.chosen_service_names.length,
                      spend: fix.budgeted.total_spend_hours,
                    })}
                {fix.budgeted.unaffordable_breaches > 0 && (
                  <span className="ms-1 text-rag-amber">
                    {t("modules.twin.warRoom.budget.unaffordable", {
                      count: fix.budgeted.unaffordable_breaches,
                    })}
                  </span>
                )}
              </p>
            )}
          </div>
        </>
      ) : (
        <p data-testid="war-room-fix-none" className="mt-1 text-sm text-slate-600">
          {t("modules.twin.warRoom.cheapestFix.none")}
        </p>
      )}
    </article>
  );
}

/** ROUND 13 G1 -- what the headline's denominator leaves out.
 *
 *  "7 of 13 breached" reads as a statement about the bank. Thirteen is
 *  the number of business services this scenario's propagation reached;
 *  an IBS with a signed, in-force tolerance that nothing in the
 *  scenario touches is not in the thirteen, is not in the table, and
 *  nothing said so. The service owner's whole question was whether his
 *  service was in the seven, and the screen answered by omission.
 *
 *  Silence is not the neutral option here: a reader who cannot find
 *  their service in a breach table concludes it is not breaching. */
const _NOT_REACHED_NAMED = 4;

/** Round 14 D1 -- a target that reached nothing, named.
 *
 *  SAMA's first scenario, a fire at the headquarters, reached 0 of 12
 *  because nothing was recorded at the head office, and the room said
 *  nothing about why. An empty table under a fire reads as a bank the
 *  fire does not touch. */
function EmptyTargetsNote({ report }: { report: ToleranceReport }) {
  const { t } = useTranslation();
  const empty = Array.isArray(report.empty_targets) ? report.empty_targets : [];
  if (empty.length === 0) return null;
  return (
    <div
      data-testid="war-room-empty-targets"
      className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      {empty.map((e) => (
        <p key={`${e.entity_kind}:${e.entity_name}`} data-kind={e.entity_kind}>
          {e.entity_kind === "facility"
            ? t("modules.twin.warRoom.emptyFacility", { name: e.entity_name })
            : e.entity_kind === "channel"
              ? t("modules.twin.warRoom.emptyChannel", { name: e.entity_name })
              : t("modules.twin.warRoom.emptyTarget", { name: e.entity_name })}
        </p>
      ))}
    </div>
  );
}

function NotReachedNote({ report }: { report: ToleranceReport }) {
  const { t } = useTranslation();
  const missing = report.not_reached ?? [];
  if (missing.length === 0) return null;
  const shown = missing.slice(0, _NOT_REACHED_NAMED);
  const names = shown.map((m) => m.ibs_name).join(", ");
  const rest = missing.length - shown.length;
  return (
    <div
      data-testid="war-room-not-reached"
      data-count={missing.length}
      data-declared={report.declared_total ?? 0}
      className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
    >
      <p className="font-medium text-slate-700">
        {t("modules.twin.warRoom.notReachedHeading", {
          count: missing.length,
          declared: report.declared_total ?? 0,
        })}
      </p>
      <p data-testid="war-room-not-reached-names" className="mt-0.5">
        {rest > 0
          ? t("modules.twin.warRoom.notReachedMore", { names, count: rest })
          : t("modules.twin.warRoom.notReachedNames", { names })}
      </p>
      <p className="mt-0.5">{t("modules.twin.warRoom.notReachedWhy")}</p>
      <Link
        to="/twin/ibs-portfolio"
        data-testid="war-room-not-reached-live"
        className="mt-1 inline-block text-brand-brassDark underline hover:no-underline"
      >
        {t("modules.twin.warRoom.notReachedLive")}
      </Link>
    </div>
  );
}

/** P204: scenario-only vs "if it hits now" (live) side by side + the delta
 *  current live conditions add on top of the modelled scenario. */
function ABCompare({ base, live }: { base: ToleranceReport; live: ToleranceReport }) {
  const { t } = useTranslation();
  const seats = useSeatHolders();
  const dIbs = live.breached - base.breached;
  const dMvb = live.mvb_breached - base.mvb_breached;
  const worse = dIbs > 0 || dMvb > 0;
  /* Rows the report reached and could not judge: no tolerance is
     declared, so there is nothing for a projection to breach.

     Round 11 F2 -- and WHICH ones. Round 10 added the count, which
     turned a silent omission into a stated one and left the reader
     holding a number: told two IBSes were left out of the reassurance,
     with no way to tell whether the one they were watching was one of
     them. The report already carries the rows. */
  const unknownRows = (live.rows ?? []).filter((r) => r.verdict === "unknown");
  /* Round 14 G -- split the rows the report could not judge by WHY. A
     declaration awaiting its second signature is not an absence, and
     saying "no tolerance is declared" about it was false to the person
     who had just declared it. */
  const pendingRows = unknownRows.filter((r) => r.tolerance_status === "pending");
  const unjudgedRows = unknownRows.filter((r) => r.tolerance_status !== "pending");
  const unjudged = unknownRows.length
    ? unjudgedRows.length
    : Math.max(0, live.assessed - live.within - live.at_risk - live.breached);
  return (
    <div
      data-testid="war-room-ab-compare"
      data-delta-ibs={dIbs}
      className={`rounded-lg border p-3 text-sm ${
        worse ? "border-rag-amber/40 bg-rag-amberBg" : "border-slate-200 bg-white"
      }`}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.warRoom.ab.scenarioOnly")}
          </span>
          <span className="text-slate-800">
            {t("modules.twin.warRoom.ab.breached", {
              breached: base.breached,
              assessed: base.assessed,
              mvb: base.mvb_breached,
            })}
            {/* Round 20 P24 -- functions judged apart, with their status. */}
            {functionsApart(base, t)}
          </span>
        </div>
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.warRoom.ab.ifNow")}
          </span>
          <span className="text-slate-800">
            {t("modules.twin.warRoom.ab.breached", {
              breached: live.breached,
              assessed: live.assessed,
              mvb: live.mvb_breached,
            })}
            {/* Round 20 P24 -- functions judged apart, with their status. */}
            {functionsApart(live, t)}
          </span>
        </div>
      </div>
      <p
        data-testid="war-room-ab-delta"
        className={`mt-2 text-xs ${worse ? "font-medium text-rag-amber" : "text-slate-500"}`}
      >
        {worse
          ? t("modules.twin.warRoom.ab.deltaWorse", { ibs: dIbs, mvb: dMvb })
          : t("modules.twin.warRoom.ab.deltaSame")}
        {/* Round 10 F3 -- "no further IBS into breach" is a claim about
            the IBSes that CAN be judged. An IBS with no declared
            tolerance has nothing to breach, so it is silently outside
            the sentence, and a reader watching a live incident on
            exactly such an IBS read the reassurance as being about it.
            Say how many were left out. */}
        {unjudged > 0 && (
          <span data-testid="war-room-ab-unjudged">
            {" "}
            {unjudgedRows.length > 0
              ? t("modules.twin.warRoom.ab.unjudgedNamed", {
                  count: unjudgedRows.length,
                  names: unjudgedRows.map((r) => r.ibs_name).join(", "),
                })
              : t("modules.twin.warRoom.ab.unjudged", { count: unjudged })}
          </span>
        )}
        {pendingRows.length > 0 && (
          <span data-testid="war-room-ab-pending">
            {" "}
            {t("modules.twin.warRoom.ab.unjudgedPending", {
              count: pendingRows.length,
              rows: pendingRows
                .map((r) => {
                  // Round 18 G23 -- with the seat list, so a pending
                  // leg names its holder.
                  const said = toleranceSentence(
                    t,
                    {
                      ibs_service_id: r.ibs_service_id,
                      ibs_code: "",
                      ibs_name: r.ibs_name,
                      mvb: r.mvb_flag,
                      hours: r.impact_tolerance_hours ?? null,
                      status: "pending",
                      signed_by: null,
                      proposed_hours: r.tolerance_proposed_hours ?? null,
                      awaiting_role: r.tolerance_awaiting_role ?? null,
                      awaiting_since: r.tolerance_awaiting_since ?? null,
                      owner_recorded: r.tolerance_owner_recorded ?? true,
                    },
                    seats.data,
                  );
                  return said ? `${r.ibs_name}: ${said}` : r.ibs_name;
                })
                .join("; "),
            })}
          </span>
        )}
      </p>
    </div>
  );
}

/** P236.3: the cheapest-fix counterpart to ABCompare -- how the remediation
 *  plan grows when current live conditions stack on top of the scenario. */
function FixABCompare({
  base,
  live,
}: {
  base: CheapestFixReport;
  live: CheapestFixReport;
}) {
  const { t } = useTranslation();
  const dServices = live.plan.steps.length - base.plan.steps.length;
  const dBreaches = live.breached - base.breached;
  const worse = dServices > 0 || dBreaches > 0;
  return (
    <div
      data-testid="war-room-fix-ab"
      data-delta-services={dServices}
      className={`rounded-lg border p-3 text-sm ${
        worse ? "border-rag-amber/40 bg-rag-amberBg" : "border-slate-200 bg-white"
      }`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
        {t("modules.twin.warRoom.fixAb.heading")}
      </p>
      <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.warRoom.ab.scenarioOnly")}
          </span>
          <span className="text-slate-800">
            {t("modules.twin.warRoom.fixAb.plan", {
              services: base.plan.steps.length,
              cleared: base.plan.total_ibs_cleared,
              ...breachCut(base, t),
            })}
          </span>
        </div>
        <div>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.warRoom.ab.ifNow")}
          </span>
          <span className="text-slate-800">
            {t("modules.twin.warRoom.fixAb.plan", {
              services: live.plan.steps.length,
              cleared: live.plan.total_ibs_cleared,
              ...breachCut(live, t),
            })}
          </span>
        </div>
      </div>
      <p
        data-testid="war-room-fix-ab-delta"
        className={`mt-2 text-xs ${worse ? "font-medium text-rag-amber" : "text-slate-500"}`}
      >
        {worse
          ? t("modules.twin.warRoom.fixAb.deltaWorse", {
              services: dServices,
              breaches: dBreaches,
            })
          : t("modules.twin.warRoom.fixAb.deltaSame")}
      </p>
    </div>
  );
}

export function WarRoomView(): JSX.Element {
  const { t, i18n } = useTranslation();
  const seats = useSeatHolders();
  // Round 18 G23 -- one sentence for the caption and the button's title:
  // the title still said "The CRO signs this pack" beside the named line.
  const croHolder = seatHolder(seats.data, "cro");
  const packWhoSigns =
    croHolder.kind === "named"
      ? t("modules.twin.warRoom.packWhoSignsNamed", { who: croHolder.name })
      : croHolder.kind === "nobody"
        ? t("modules.twin.warRoom.packWhoSignsUnheld")
        : t("modules.twin.warRoom.packWhoSigns");
  const scenarios = useScenarios();
  const liveNow = useLiveTolerance();
  const hasLive = (liveNow.data?.rows ?? []).some((r) => r.verdict in LIVE_ORDER);
  // E4 -- a recommendation about one scenario used to land on the war
  // room's empty picker, leaving the reader to find the scenario the
  // recommendation was about.
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(params.get("scenario"));
  const [budget, setBudget] = useState<number | null>(null);
  const [basis, setBasis] = useState<CostBasis>("hours");
  const [live, setLive] = useState(false);
  // P204 A/B: fetch BOTH the scenario-only verdict and the "if it hits now"
  // (live) verdict, so the compare banner shows both at once. The toggle
  // picks which one drives the detailed table + cheapest-fix.
  const baseReport = useScenarioTolerance(selected, false);
  const liveReport = useScenarioTolerance(selected, true);
  const report = live ? liveReport : baseReport;
  // P236.3 A/B: fetch BOTH cheapest-fixes (scenario-only + "if it hits now"),
  // so the fix compare banner shows how remediation grows under live
  // conditions. The toggle picks which one drives the detailed panel.
  const baseFix = useScenarioCheapestFix(selected, budget, basis, false);
  const liveFix = useScenarioCheapestFix(selected, budget, basis, true);
  const fix = live ? liveFix : baseFix;
  const toast = useToast();
  const pack = useDownloadWarRoomPack();
  // The endpoint requires the approver position (admin admitted).
  const me = useMe();
  const canDownloadPack =
    me.data?.is_admin === true || hasPosition(me.data, "approver");
  const onDownloadPack = () => {
    if (!selected) return;
    pack.mutate(
      { scenarioId: selected, live },
      {
        onSuccess: (r) =>
          toast.push({
            tone: "success",
            message: t("modules.twin.warRoom.packDownloaded"),
            detail: r.filename,
          }),
        onError: (e) =>
          toast.push({
            tone: e.status === 403 ? "warning" : "error",
            message: t("modules.twin.warRoom.packFailed"),
            detail: e.detail,
          }),
      },
    );
  };

  return (
    <section data-testid="war-room-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("modules.twin.warRoom.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="war-room"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.twin.warRoom.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("modules.twin.warRoom.intro")}
        </p>
      </header>

      <LiveNowPanel />
      {hasLive && (scenarios.data?.length ?? 0) > 0 && (
        <p
          data-testid="war-room-rehearse-heading"
          className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400"
        >
          {t("modules.twin.warRoom.liveNow.rehearse")}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {(scenarios.data ?? []).map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`war-room-scenario-${s.code}`}
            onClick={() => setSelected(s.id)}
            className={`rounded-lg border px-3 py-2 text-start text-sm ${
              selected === s.id
                ? "border-brand-brass/50 bg-brand-brass/5 text-brand-brassDark"
                : "border-slate-200 bg-white text-slate-700 hover:border-brand-brass/30"
            }`}
          >
            {s.name}
            {/* ROUND 15 D12 -- a member is marked, not hidden: the
                composite runs it, and it can still be run alone. */}
            {(s.member_of?.length ?? 0) > 0 && (
              <span
                data-testid={`war-room-scenario-member-of-${s.code}`}
                className="block text-[10px] text-slate-500"
              >
                {t("modules.twin.warRoom.partOf", {
                  names: s.member_of!.map((m) => m.name).join(", "),
                })}
              </span>
            )}
          </button>
        ))}
      </div>

      {selected && (
        <div className="flex flex-wrap items-center gap-4">
          <label
            data-testid="war-room-live-toggle"
            className="flex w-fit items-center gap-2 text-sm text-slate-700"
          >
            <input
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
              className="accent-brand-brass"
            />
            {t("modules.twin.warRoom.liveToggle")}
          </label>
          {/* E4 -- the pack is approver-gated on the server, and the
              button was offered to everybody: you learned you could
              not have it by being refused after asking. Say who signs
              it instead. */}
          <button
            type="button"
            data-testid="war-room-download-pack"
            onClick={onDownloadPack}
            disabled={pack.isPending || !canDownloadPack}
            title={canDownloadPack ? undefined : packWhoSigns}
            className="rounded-md border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-60"
          >
            {pack.isPending
              ? t("modules.twin.warRoom.packDownloading")
              : t("modules.twin.warRoom.downloadPack")}
          </button>
          {!canDownloadPack && (
            <span
              data-testid="war-room-pack-who-signs"
              className="text-xs text-slate-500"
            >
              {/* ROUND 15 G2 -- by name where the record names one. */}
              {packWhoSigns}
            </span>
          )}
        </div>
      )}

      {/* Round 12 A6 -- "Choose a scenario to stress-test" rendered
          whether or not one existed, so on a new bank it asked her to
          pick from an empty row of buttons. Nothing to pick from and
          nothing picked are different situations and now read as such;
          the library is a Twin surface, so this route admits her. */}
      {!selected && scenarios.data && scenarios.data.length === 0 && (
        <EmptyState
          testid="war-room-empty"
          message={t("modules.twin.warRoom.emptyNoScenarios")}
          cta={{
            label: t("modules.twin.warRoom.emptyNoScenariosCta"),
            to: "/twin/scenarios",
          }}
        />
      )}
      {!selected && (scenarios.data?.length ?? 0) > 0 && (
        <p data-testid="war-room-pick" className="text-sm text-slate-500">
          {t("modules.twin.warRoom.pickPrompt")}
        </p>
      )}
      {selected && report.isLoading && (
        <p data-testid="war-room-loading" className="text-sm text-slate-500">
          {t("modules.twin.warRoom.loading")}
        </p>
      )}
      {selected && report.isError && (
        <p data-testid="war-room-error" className="text-sm text-rag-red">
          {t("modules.twin.warRoom.error")}
        </p>
      )}

      {selected && baseReport.data && liveReport.data && (
        <ABCompare base={baseReport.data} live={liveReport.data} />
      )}

      {report.data && (
        <article
          data-testid="war-room-report"
          className="rounded-lg border border-slate-200 bg-white p-4"
        >
          <div
            data-testid="war-room-headline"
            className={`mb-3 rounded-md px-3 py-2 text-sm font-semibold ${
              report.data.breached > 0
                ? "bg-rag-redBg text-rag-red"
                : "bg-rag-greenBg text-rag-green"
            }`}
          >
            {t("modules.twin.warRoom.breachedHeadline", {
              breached: report.data.breached,
              assessed: report.data.assessed,
            })}
            {(report.data.candidates_assessed ?? 0) > 0 && (
              <span
                data-testid="war-room-candidates"
                className="ms-2 text-xs font-normal [unicode-bidi:isolate]"
              >
                <HeadlineSep />
                {t("modules.twin.warRoom.candidatesHeadline", {
                  count: report.data.candidates_assessed ?? 0,
                  breached: report.data.candidates_breached ?? 0,
                })}
              </span>
            )}
            {(report.data.functions_assessed ?? 0) > 0 && (
              <span
                data-testid="war-room-functions"
                className="ms-2 text-xs font-normal [unicode-bidi:isolate]"
              >
                <HeadlineSep />
                {t("modules.twin.warRoom.functionsHeadline", {
                  count: report.data.functions_assessed ?? 0,
                  breached: report.data.functions_breached ?? 0,
                })}
              </span>
            )}
            {report.data.mvb_breached > 0 && (
              <span className="ms-2 rounded bg-white/60 px-1.5 py-0.5 text-xs [unicode-bidi:isolate]">
                <HeadlineSep />
                {(report.data.candidates_mvb_breached ?? 0) > 0
                  ? t("modules.twin.warRoom.mvbBreachedAdmitted", {
                      count: report.data.mvb_breached,
                      admitted:
                        report.data.mvb_breached -
                        (report.data.candidates_mvb_breached ?? 0),
                    })
                  : t("modules.twin.warRoom.mvbBreached", {
                      count: report.data.mvb_breached,
                    })}
              </span>
            )}
            {/* P237.2: of the breaching IBSes, how many carry a signed
                (board-governed) tolerance -- the governance coverage chip. */}
            {report.data.breached > 0 &&
              (() => {
                // Stitching review 5.4 -- the server's count, the one the
                // signed pack prints, over the services. This counted it
                // here over breached rows, functions included, so the
                // chip and the pack disagreed. Older payloads fall back.
                const governed =
                  report.data.governed_breached ??
                  report.data.rows.filter(
                    (r: ToleranceRow) =>
                      r.verdict === "breached" && r.tolerance_provenance,
                  ).length;
                return (
                  <Link
                    // Round 17 H37, tenth round -- My Work held nothing for
                    // a seat that signs no tolerance; the tolerances behind
                    // the count are on the dimensions page. A signer still
                    // goes to where they sign.
                    to={
                      canSignAs(me.data, "cro") || canSignAs(me.data, "business_owner")
                        ? "/my-work"
                        : "/twin/tolerance-metrics"
                    }
                    data-testid="war-room-governed"
                    data-governed={governed}
                    className="ms-2 rounded bg-white/60 px-1.5 py-0.5 text-xs [unicode-bidi:isolate] underline hover:no-underline"
                    // A3 -- the chip names a governance gap and had
                    // nowhere to close it. The tolerances it counts are
                    // signed from My Work, which is where the CRO's
                    // ceremony queue lives.
                    title={t("modules.twin.warRoom.governedChipHint")}
                  >
                    <HeadlineSep />
                    {t("modules.twin.warRoom.governedChip", {
                      governed,
                      total: report.data.breached,
                    })}
                  </Link>
                );
              })()}
            {report.data.live_baseline_applied &&
              (report.data.live_impacted_count ?? 0) > 0 && (
                <span
                  data-testid="war-room-live-impacted"
                  className="ms-2 rounded bg-white/60 px-1.5 py-0.5 text-xs [unicode-bidi:isolate]"
                >
                  <HeadlineSep />
                  {t("modules.twin.warRoom.liveImpacted", {
                    count: report.data.live_impacted_count ?? 0,
                  })}
                </span>
              )}
            {report.data.soonest_breach_hours != null && (
              <span
                data-testid="war-room-soonest-breach"
                className="ms-2 rounded bg-white/60 px-1.5 py-0.5 text-xs [unicode-bidi:isolate]"
              >
                <HeadlineSep />
                {t("modules.twin.warRoom.soonestBreach", {
                  n: report.data.soonest_breach_hours,
                })}
                {/* Round 16 D21 -- the countdown ran against a figure
                    nobody signed, and said nothing of it. */}
                {report.data.rows.some(
                  (r: ToleranceRow) =>
                    r.breaches_at_hours === report.data.soonest_breach_hours &&
                    r.tolerance_status === "set_unsigned",
                ) && (
                  <span data-testid="war-room-soonest-unsigned">
                    {" "}
                    {t("modules.twin.warRoom.soonestUnsigned")}
                  </span>
                )}
              </span>
            )}
            {(report.data.total_service_hours_lost ?? 0) > 0 && (
              <span
                data-testid="war-room-service-hours-lost"
                className="ms-2 rounded bg-white/60 px-1.5 py-0.5 text-xs [unicode-bidi:isolate]"
              >
                <HeadlineSep />
                {t("modules.twin.warRoom.totalServiceHoursLost", {
                  n: report.data.total_service_hours_lost,
                })}
              </span>
            )}
          </div>
          <EmptyTargetsNote report={report.data} />
          <NotReachedNote report={report.data} />
          {/* P272.1 (UX-2 J4-5 / J4-3): close the act loop -- a breach
              offers the way OUT (recommendations) and the way to fix the
              governance gap the chip exposes (declare/sign tolerances,
              which live in the Foundation IBS register drawer). */}
          {report.data.breached > 0 &&
            (() => {
              // The breaching IBS whose tolerance nobody has signed
              // for -- that is the one the governance link is about.
              // B1 -- a row with a declaration already in flight does
              // not need declaring; it needs signing. Offering
              // "Declare tolerances" underneath it told the person who
              // had just declared one that their work had not
              // happened.
              // C1 -- excluding "pending" was not enough. A row reading
              // "2h declared, not yet signed" carries status
              // `set_unsigned`: the figure EXISTS, it is the signature
              // that does not, and the invitation still told the
              // person who had declared it that their work had not
              // happened.
              //
              // Only a row with no declaration at all needs declaring.
              // A declared-but-unsigned one needs signing, and the
              // badge beside it already says so.
              const undeclared =
                report.data.rows.find(
                  (r: ToleranceRow) =>
                    r.verdict === "breached" &&
                    !r.tolerance_provenance &&
                    (r.tolerance_status == null || r.tolerance_status === "undeclared"),
                ) ?? null;
              return (
                <div
                  data-testid="war-room-actions"
                  className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-sm"
                >
                  <Link
                    to="/recommendations"
                    data-testid="war-room-recommendations-link"
                    className="font-medium text-brand-brassDark hover:underline"
                  >
                    {t("modules.twin.warRoom.actions.recommendations")}
                  </Link>
                  {/* E4 -- this pointed at the admin IBS register: a
                  list, admin-gated, with no indication which IBS or
                  where the form is. The declaration form lives on the
                  IBS's own page, so go to the one that needs it. */}
                  {undeclared ? (
                    <Link
                      to={`/twin/ibs/${undeclared.ibs_service_id}`}
                      data-testid="war-room-govern-link"
                      className="font-medium text-brand-brassDark hover:underline"
                    >
                      {t("modules.twin.warRoom.actions.declareFor", {
                        ibs: undeclared.ibs_name,
                      })}
                    </Link>
                  ) : null}
                </div>
              );
            })()}
          <table className="w-full text-start text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-400">
              <tr>
                {/* Round 19 L15 -- where a function is among the rows, the
                    column says so; it read "IBS" over the treasurer's
                    own function. */}
                <th className="py-1" data-testid="war-room-col-subject">
                  {report.data.rows.some((r) => r.is_function)
                    ? t("modules.twin.warRoom.colIbsOrFunction")
                    : t("modules.twin.warRoom.colIbs")}
                </th>
                <th className="py-1">{t("modules.twin.warRoom.colProjected")}</th>
                <th className="py-1">{t("modules.twin.warRoom.colTolerance")}</th>
                <th className="py-1">{t("modules.twin.warRoom.colClock")}</th>
                <th className="py-1">{t("modules.twin.warRoom.colRecovery")}</th>
                <th className="py-1">{t("modules.twin.warRoom.colWeakest")}</th>
                <th className="py-1">{t("modules.twin.warRoom.colVerdict")}</th>
              </tr>
            </thead>
            <tbody>
              {report.data.rows.map((r: ToleranceRow) => (
                <tr
                  key={r.ibs_service_id}
                  data-testid={`war-room-row-${r.ibs_service_id}`}
                  className="border-t border-slate-100"
                >
                  <td className="py-1.5 text-slate-800">
                    {/* A3 -- my regression, and the fix was worse than
                        the defect. Round 5's contradiction was a
                        declare invitation under a row already declared;
                        I made the invitation conditional, and on this
                        estate every breaching row HAS a declaration, so
                        the condition was never true and the only
                        outbound link on the screen disappeared.
                        Correcting the arithmetic and deleting the route
                        took a step from 3/3/2 to 0/1/0: the screen that
                        identifies unsigned tolerances now offered
                        nothing to do about them.
                        Every named IBS links to its page. */}
                    <Link
                      to={`/twin/ibs/${r.ibs_service_id}`}
                      data-testid={`war-room-ibs-link-${r.ibs_service_id}`}
                      className="text-brand-brassDark hover:underline"
                    >
                      <Bdi>{r.ibs_name}</Bdi>
                    </Link>
                    {r.is_function ? (
                      <span
                        data-testid="war-room-function"
                        className="ms-1 text-[10px] font-semibold uppercase text-teal-800"
                      >
                        {t("vocab.entityKind.ibs_service_internal")}
                      </span>
                    ) : (
                      r.mvb_flag && (
                        <span className="ms-1 text-[10px] font-semibold uppercase text-brand-brassDark">
                          {t("screens.warRoomView.mvb")}
                        </span>
                      )
                    )}
                    {r.propagated_via && (
                      <span
                        data-testid="war-room-propagated-via"
                        data-live={r.via_live ? "true" : "false"}
                        className={`ms-1 text-[10px] italic ${
                          r.via_live ? "text-rag-amber" : "text-slate-400"
                        }`}
                      >
                        {r.via_funding
                          ? t("modules.twin.warRoom.propagatedViaFunding", {
                              name: r.propagated_via,
                            })
                          : t("modules.twin.warRoom.propagatedVia", {
                              name: r.propagated_via,
                            })}
                      </span>
                    )}
                    <CapacityLossNote row={r} />
                    <StatedFiguresNote row={r} />
                  </td>
                  <td className="py-1.5 tabular-nums text-slate-600">
                    {t("modules.twin.warRoom.hours", { n: r.projected_outage_hours })}
                    <StatedDurationNote row={r} />
                    {(r.still_working_channels ?? []).length > 0 && (
                      /* Round 16 D22 -- why the projection was lowered:
                         the scenario darkens some channels and this
                         service is still delivered through others. */
                      <span
                        data-testid="war-room-still-working"
                        className="block text-[10px] text-slate-500"
                      >
                        {t("modules.twin.warRoom.stillWorking", {
                          channels: new Intl.ListFormat(i18n.language, {
                            style: "long",
                            type: "conjunction",
                          }).format(r.still_working_channels ?? []),
                        })}
                      </span>
                    )}
                    {r.upstream_rto_hours != null && (
                      <span
                        data-testid="war-room-upstream-bound"
                        title={t("modules.twin.warRoom.upstreamBoundTip")}
                        className="ms-1 text-[10px] italic text-slate-400"
                      >
                        {t("modules.twin.warRoom.upstreamBound", {
                          n: r.upstream_rto_hours,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 tabular-nums text-slate-600">
                    <ToleranceCell row={r} />
                  </td>
                  <td className="py-1.5 tabular-nums">
                    <ClockCell row={r} />
                  </td>
                  <td className="py-1.5">
                    <RecoveryCell row={r} />
                  </td>
                  <td className="py-1.5 text-slate-500">{r.binding_service ?? "--"}</td>
                  <td className="py-1.5">
                    <VerdictPill verdict={r.verdict} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      )}

      {report.data && report.data.breached > 0 && baseFix.data && liveFix.data && (
        <FixABCompare base={baseFix.data} live={liveFix.data} />
      )}

      {report.data && report.data.breached > 0 && fix.data && (
        <CheapestFixPanel
          fix={fix.data}
          budget={budget}
          onBudget={setBudget}
          basis={basis}
          onBasis={setBasis}
        />
      )}
    </section>
  );
}

/** Round 17 P3 -- the plan clears functions as well as services, so the
 *  denominator counts both and the sentence names the functions. */
export function breachCut(
  fix: CheapestFixReport,
  t: TFunction,
): { breached: number; fnCut: string } {
  const fn = fix.functions_breached ?? 0;
  return {
    breached: fix.breached + fn,
    fnCut: fn > 0 ? t("modules.twin.warRoom.fixPlan.fnCut", { count: fn }) : "",
  };
}

/** Round 20 P24 -- the functions the report judges apart from the
 *  services, with their register status; empty when there are none. */
function functionsApart(report: ToleranceReport, t: TFunction): string {
  const fns = report.functions_assessed ?? 0;
  if (fns === 0) return "";
  const cand = report.functions_candidates_assessed ?? 0;
  const status =
    cand === fns
      ? t("modules.twin.thread.scenario.judgedFnUnderAssessment")
      : cand === 0
        ? t("modules.twin.thread.scenario.judgedFnAdmitted")
        : t("modules.twin.thread.scenario.judgedFnSome", { n: cand });
  return (
    "; " +
    t("modules.twin.warRoom.ab.functionsApart", {
      count: fns,
      status,
      breached: report.functions_breached ?? 0,
    })
  );
}
